import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { getUTM } from "../lib/api";
import { toast } from "sonner";

export default function MagazineModal({ openState, sourcePage, onClose }) {
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: "",
    email: "",
    brand_or_dealership: "",
    persona: "",
    consent: false,
  });

  useEffect(() => {
    if (openState) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }, [openState]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please confirm consent to continue.");
      return;
    }
    setSubmitting(true);
    try {
      const utm = getUTM();
      const { data } = await api.post("/subscribe", {
        ...form,
        source_page: sourcePage || "home",
        campaign: utm.utm_campaign || "nocturne-magazine",
        edition: "issue-01",
        ...utm,
      });
      sessionStorage.setItem("gb_nocturne_unlocked", "1");
      toast.success(data.message || "You're in.");
      onClose();
      navigate(`/nocturne/welcome?fn=${encodeURIComponent(form.first_name || "")}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {openState && (
        <motion.div
          data-testid="magazine-modal"
          className="fixed inset-0 z-[100] flex items-center justify-center px-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/85 backdrop-blur-2xl" onClick={onClose} />
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-xl bg-[#0a0a0a] border border-white/10 z-10 noise"
          >
            <button
              data-testid="magazine-modal-close"
              onClick={onClose}
              className="absolute top-4 right-4 p-2 text-white/60 hover:text-[var(--gb-gold)]"
              aria-label="close"
            >
              <X size={20} />
            </button>

            <div className="p-10 md:p-14">
              <p className="overline mb-4">Good Bandit · Nocturne</p>
                <h2 className="font-display text-4xl md:text-5xl text-white leading-tight mb-4">
                  Join <span className="italic text-[var(--gb-gold)]">Nocturne.</span>
                </h2>
                <p className="text-sm text-white/65 leading-relaxed mb-8 max-w-md">
                  Download the inaugural issue of Good Bandit Nocturne and receive future
                  editions, cinematic films, behind the scenes stories and editorial releases.
                </p>

                <form onSubmit={submit} className="space-y-6">
                  <div>
                    <label className="kbd block mb-2">FIRST NAME</label>
                    <input
                      data-testid="magazine-firstname-input"
                      value={form.first_name}
                      onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                      className="gb-input" required placeholder="Jane"
                    />
                  </div>
                  <div>
                    <label className="kbd block mb-2">EMAIL ADDRESS</label>
                    <input
                      data-testid="magazine-email-input"
                      type="email" value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="gb-input" required placeholder="you@example.com"
                    />
                  </div>
                  <div>
                    <label className="kbd block mb-2">COMPANY <span className="text-white/30 ml-2">OPTIONAL</span></label>
                    <input
                      data-testid="magazine-brand-input"
                      value={form.brand_or_dealership}
                      onChange={(e) => setForm({ ...form, brand_or_dealership: e.target.value })}
                      className="gb-input" placeholder="Porsche of Vancouver"
                    />
                  </div>
                  <div>
                    <label className="kbd block mb-2">I AM…</label>
                    <select
                      data-testid="magazine-persona-select"
                      value={form.persona}
                      onChange={(e) => setForm({ ...form, persona: e.target.value })}
                      className="gb-input bg-black text-white"
                      required
                    >
                      <option value="" disabled>Select one</option>
                      {[
                        "Luxury Enthusiast",
                        "Dealership",
                        "Manufacturer",
                        "Photographer",
                        "Creator",
                        "Journalist",
                        "Marketing Professional",
                        "Other",
                      ].map((p) => (
                        <option key={p} value={p} className="bg-black">{p}</option>
                      ))}
                    </select>
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer pt-2">
                    <input
                      data-testid="magazine-consent-checkbox"
                      type="checkbox" checked={form.consent}
                      onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                      className="mt-1 w-4 h-4 accent-[var(--gb-gold)] bg-transparent"
                    />
                    <span className="text-xs text-white/60 leading-relaxed">
                      I agree to receive updates from Good Bandit. CASL/GDPR compliant.
                      Unsubscribe anytime.
                    </span>
                  </label>

                  <button
                    data-testid="magazine-submit-button"
                    type="submit" disabled={submitting}
                    className="gb-btn-primary w-full justify-center"
                  >
                    {submitting ? "Unlocking…" : "Unlock Issue 01"}
                  </button>
                  <p className="kbd text-center text-white/30 pt-2">
                    BY SUBMITTING YOU AGREE TO OUR PRIVACY POLICY
                  </p>
                </form>
              </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
